import { Check, ChevronLeft, ChevronRight, Home, ImageIcon, Maximize2, RefreshCcw, Ruler, Sparkles, Upload } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export const GENERATION_STAGES = ["Analysing window", "Applying fabric", "Matching pattern", "Rendering preview"];

export const previewSpecs = (p) => [p?.metadata?.specs?.colour || p?.metadata?.specs?.fabric, p?.metadata?.specs?.pleat].filter(Boolean).join(" · ");

function OverlayButton({ className, children, ...props }) {
  return (
    <button
      type="button"
      className={cn("h-10 w-10 rounded-full bg-card/95 text-foreground shadow-md flex items-center justify-center hover:bg-card transition-colors disabled:opacity-40", className)}
      {...props}
    >
      {children}
    </button>
  );
}

// The big picture: the AI preview being viewed, or the customer's photo, with
// prev/next through the history and a toggle back to the original photo.
export default function PreviewStage({
  preview,
  previewIndex,
  previewCount,
  onStep,
  showOriginal,
  setShowOriginal,
  sourceUrl,
  isGenerating,
  stageIdx,
  onFullscreen,
  onRetry,
  onUpload,
  uploading,
  window: win,
  roomName,
}) {
  const hasPreviewImage = preview?.status === "SUCCESS" && preview?.previewUrl;
  const showingOriginal = showOriginal || !preview;
  const imageUrl = showingOriginal ? sourceUrl : hasPreviewImage ? preview.previewUrl : null;

  return (
    <section className="bg-card border border-border rounded-2xl overflow-hidden flex flex-col" data-testid="ai-preview-hero-section">
      <div className="relative aspect-[4/3] bg-secondary flex items-center justify-center overflow-hidden">
        {/* Under a running job the customer's photo stays visible, softened, behind the progress. */}
        {isGenerating && !showingOriginal ? (
          <>
            {sourceUrl && <img src={sourceUrl} alt="" className="absolute inset-0 h-full w-full object-cover blur-sm scale-105 opacity-60" />}
            <div className="relative flex flex-col items-center gap-4 rounded-2xl bg-foreground/75 px-8 py-6 text-background" data-testid="ai-generation-progress">
              <Sparkles className="h-6 w-6 text-[#E8B98E] animate-pulse" />
              <p className="font-serif text-lg">Creating your design</p>
              <div className="flex flex-col items-center gap-1.5">
                {GENERATION_STAGES.map((stage, idx) => (
                  <p key={stage} className={cn("text-sm transition-opacity duration-300", idx === stageIdx ? "opacity-100 text-[#E8B98E]" : "opacity-40")}>
                    {stage}
                  </p>
                ))}
              </div>
            </div>
          </>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={showingOriginal ? "Customer window" : `AI preview #${previewCount - previewIndex}`}
            data-testid={showingOriginal ? "source-window-photo" : `ai-preview-image-${preview._id}`}
            onClick={showingOriginal ? undefined : onFullscreen}
            className={cn("h-full w-full object-contain hi-animate-fade-up", !showingOriginal && "cursor-zoom-in")}
          />
        ) : preview?.status === "FAILED" && !showingOriginal ? (
          <div className="text-center px-6" data-testid={`ai-preview-error-${preview._id}`}>
            <p className="text-sm text-muted-foreground">Generation failed: {preview.errorMessage}</p>
            <Button variant="outline" size="sm" className="mt-3 bg-card" data-testid={`retry-preview-button-${preview._id}`} onClick={onRetry}>
              <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Retry
            </Button>
          </div>
        ) : (
          <label className="flex flex-col items-center gap-2 text-muted-foreground cursor-pointer px-6 text-center">
            <span className="h-14 w-14 rounded-full bg-card border border-border flex items-center justify-center">
              <ImageIcon className="h-6 w-6" strokeWidth={1.5} />
            </span>
            <span className="text-sm font-medium text-foreground">Upload the customer's window photo</span>
            <span className="text-xs">The AI dresses this photo with the fabric you choose.</span>
            <input data-testid={preview || imageUrl ? undefined : "upload-window-photo-input"} type="file" accept="image/*" className="hidden" onChange={onUpload} />
          </label>
        )}

        {/* Top-left: what's on screen, and a switch between preview and original */}
        {(preview || sourceUrl) && (
          <div className="absolute top-3 left-3 flex items-center gap-2">
            {preview && sourceUrl ? (
              <div className="flex rounded-full bg-card/95 p-1 shadow-sm text-xs" role="group" aria-label="Image shown">
                {[
                  { v: false, label: "AI Preview", icon: Sparkles },
                  { v: true, label: "Original", icon: ImageIcon },
                ].map(({ v, label, icon: Icon }) => (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={showOriginal === v}
                    data-testid={`stage-toggle-${v ? "original" : "preview"}`}
                    onClick={() => setShowOriginal(v)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors",
                      showOriginal === v ? "bg-foreground text-background" : "text-foreground/75 hover:text-foreground",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
              </div>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-card/95 px-3 py-1.5 text-xs shadow-sm">
                {preview ? <Sparkles className="h-3.5 w-3.5" /> : <ImageIcon className="h-3.5 w-3.5" />}
                {preview ? "AI Preview" : "Customer photo"}
              </span>
            )}
            {!showingOriginal && preview?.isSelectedFinal && (
              <span className="inline-flex items-center gap-1 rounded-full bg-success text-white px-2.5 py-1.5 text-xs shadow-sm">
                <Check className="h-3.5 w-3.5" /> Final design
              </span>
            )}
          </div>
        )}

        {!showingOriginal && hasPreviewImage && !isGenerating && (
          <OverlayButton aria-label="View full screen" title="View full screen" data-testid="ai-preview-fullscreen-button" onClick={onFullscreen} className="absolute top-3 right-3">
            <Maximize2 className="h-4 w-4" />
          </OverlayButton>
        )}

        {!showingOriginal && previewCount > 1 && (
          <>
            <OverlayButton aria-label="Newer preview" data-testid="stage-prev" disabled={previewIndex <= 0} onClick={() => onStep(-1)} className="absolute left-3 top-1/2 -translate-y-1/2">
              <ChevronLeft className="h-5 w-5" />
            </OverlayButton>
            <OverlayButton aria-label="Older preview" data-testid="stage-next" disabled={previewIndex >= previewCount - 1} onClick={() => onStep(1)} className="absolute right-3 top-1/2 -translate-y-1/2">
              <ChevronRight className="h-5 w-5" />
            </OverlayButton>
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-foreground/70 text-background px-2.5 py-1 text-xs tabular-nums">
              #{previewCount - previewIndex} of {previewCount}
            </span>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3.5 border-t border-border text-sm text-foreground/85">
        <span className="inline-flex items-center gap-2 font-mono-spec">
          <Ruler className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          {win ? `${win.width} × ${win.height} ${win.unit}` : "—"}
        </span>
        {roomName && (
          <span className="inline-flex items-center gap-2 pl-5 border-l border-border">
            <Home className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            {roomName}
          </span>
        )}
        {!showingOriginal && previewSpecs(preview) && <span className="text-muted-foreground truncate hidden sm:inline">{previewSpecs(preview)}</span>}
        <label className="ml-auto inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2 text-sm font-medium cursor-pointer hover:bg-secondary transition-colors">
          <Upload className="h-4 w-4" />
          {uploading ? "Uploading…" : sourceUrl ? "Replace Image" : "Upload Image"}
          {/* Exactly one file input carries the test id: this one, unless the empty state is up. */}
          <input data-testid={preview || imageUrl ? "upload-window-photo-input" : undefined} type="file" accept="image/*" className="hidden" onChange={onUpload} />
        </label>
      </div>
    </section>
  );
}
